import { answerDisplayParts } from '@/lib/audition/answers'
import type { AuditionAnswer } from '@/lib/audition/steps'
import { QUESTION_STEPS } from '@/lib/audition/steps'

/**
 * Read-only view of what a returning applicant already sent us, shown next
 * to the resubmit button so they can see what they are re-submitting.
 *
 * Driven by QUESTION_STEPS rather than by the stored keys, so a question
 * added later cannot silently drop out of the recap — and a key whose field
 * has since been removed is skipped rather than rendered without a label.
 */
export function AnswerRecap({
  answers,
}: {
  answers: Record<string, Record<string, AuditionAnswer>>
}) {
  const rows = QUESTION_STEPS.flatMap((step) =>
    step.fields
      .map((field) => ({
        key: `${step.id}.${field.name}`,
        label: field.label,
        // Rendered in the field's own vocabulary — the ladder stores move
        // ids, and a returning applicant should be shown the sentence they
        // tapped, not "up".
        // Read tolerantly, through the one reader the admin page uses too:
        // a question that moved steps or was renamed still holds its answer
        // under the old step id or name, and a returning applicant seeing a
        // blank where their own answer is would reasonably conclude we lost
        // it. An Other chip carries what they typed beside it.
        value: answerDisplayParts(answers, step.id, field).join(' · '),
      }))
      .filter((row) => row.value !== ''),
  )

  if (rows.length === 0) return null

  return (
    <dl className="space-y-3 rounded-md border border-border bg-muted/40 px-4 py-3 text-sm">
      {rows.map((row) => (
        <div key={row.key} className="space-y-0.5">
          <dt className="font-medium">{row.label}</dt>
          <dd className="whitespace-pre-wrap text-muted-foreground">
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  )
}
